import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Save, Edit3, Sliders, Image as ImageIcon } from 'lucide-react';
import { X, RotateCcw, Upload, Sparkles } from './icons';
import { useToast } from '../contexts/ToastContext';

const SignatureDrawer = ({ isOpen, onClose, onSave, title = "Crear tu firma digital" }) => {
    const { showToast } = useToast();
    const canvasRef = useRef(null);
    const fileInputRef = useRef(null);

    const [activeTab, setActiveTab] = useState('draw'); // 'draw' | 'upload'
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasSignature, setHasSignature] = useState(false);
    const [context, setContext] = useState(null);

    // Estado para la imagen subida
    const [rawImage, setRawImage] = useState(null);
    const [removeBg, setRemoveBg] = useState(true);
    const [bgThreshold, setBgThreshold] = useState(80);

    const initCanvas = useCallback(() => {
        if (!canvasRef.current) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        const containerWidth = canvas.parentElement ? canvas.parentElement.clientWidth : 400;
        canvas.width = Math.min(420, containerWidth - 32);
        canvas.height = 200;

        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        setContext(ctx);
    }, []);

    useEffect(() => {
        if (isOpen) {
            setTimeout(initCanvas, 50);
            setHasSignature(false);
            setRawImage(null);
            setActiveTab('draw');
        }
    }, [isOpen, initCanvas]);

    // Renderizar imagen subida con eliminación de fondo blanco/claro
    const renderUploadedImage = useCallback(() => {
        if (!rawImage || !canvasRef.current || !context) return;
        const canvas = canvasRef.current;
        context.clearRect(0, 0, canvas.width, canvas.height);

        // Escalar imagen para encajar proporcionalmente en el canvas
        const scale = Math.min((canvas.width - 20) / rawImage.width, (canvas.height - 20) / rawImage.height);
        const w = rawImage.width * scale;
        const h = rawImage.height * scale;
        const x = (canvas.width - w) / 2;
        const y = (canvas.height - h) / 2;

        context.drawImage(rawImage, x, y, w, h);

        if (removeBg) {
            const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;
            const w = canvas.width;
            const h = canvas.height;

            // 1. Muestrear el color real del fondo desde el borde de la imagen,
            // en vez de asumir blanco puro (255,255,255). Fotos/escaneos de firmas
            // suelen tener papel grisáceo, con sombras o manchas irregulares -- un
            // umbral fijo de "blanco" falla justo en esos casos difíciles.
            const margin = Math.max(2, Math.round(Math.min(w, h) * 0.05));
            let sumR = 0, sumG = 0, sumB = 0, count = 0;
            for (let y = 0; y < h; y++) {
                const onBorderRow = y < margin || y >= h - margin;
                for (let x = 0; x < w; x++) {
                    if (!onBorderRow && x >= margin && x < w - margin) continue;
                    const i = (y * w + x) * 4;
                    if (data[i + 3] === 0) continue; // fuera del área dibujada
                    sumR += data[i];
                    sumG += data[i + 1];
                    sumB += data[i + 2];
                    count++;
                }
            }
            const bgR = count ? sumR / count : 255;
            const bgG = count ? sumG / count : 255;
            const bgB = count ? sumB / count : 255;

            // 2. Umbral suave por distancia de color al fondo muestreado (no solo
            // luminancia), con rampa de transparencia en vez de corte binario --
            // evita bordes dentados y maneja sombras/manchas parciales del papel.
            const highT = bgThreshold; // distancia a partir de la cual es 100% tinta
            const lowT = bgThreshold * 0.45; // por debajo, 100% fondo/transparente
            for (let i = 0; i < data.length; i += 4) {
                const dr = data[i] - bgR;
                const dg = data[i + 1] - bgG;
                const db = data[i + 2] - bgB;
                const dist = Math.sqrt(dr * dr + dg * dg + db * db);
                if (dist <= lowT) {
                    data[i + 3] = 0;
                } else if (dist < highT) {
                    const ratio = (dist - lowT) / (highT - lowT);
                    data[i + 3] = Math.round(data[i + 3] * ratio);
                }
            }
            context.putImageData(imageData, 0, 0);
        }
        setHasSignature(true);
    }, [rawImage, context, removeBg, bgThreshold]);

    useEffect(() => {
        if (activeTab === 'upload' && rawImage) {
            renderUploadedImage();
        }
    }, [activeTab, rawImage, removeBg, bgThreshold, renderUploadedImage]);

    const clearCanvas = () => {
        if (context && canvasRef.current) {
            context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
            setHasSignature(false);
            setRawImage(null);
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            showToast('Formato no válido', 'Por favor selecciona un archivo de imagen (PNG, JPG, WEBP)', 'warning');
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                setRawImage(img);
                setActiveTab('upload');
            };
            img.src = event.target.result;
        };
        reader.readAsDataURL(file);
    };

    // Eventos de dibujo manual
    const startDrawing = (e) => {
        if (activeTab !== 'draw') return;
        setIsDrawing(true);
        const rect = canvasRef.current.getBoundingClientRect();
        const clientX = e.clientX || (e.touches && e.touches[0].clientX);
        const clientY = e.clientY || (e.touches && e.touches[0].clientY);
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        context.beginPath();
        context.moveTo(x, y);
    };

    const draw = (e) => {
        if (!isDrawing || activeTab !== 'draw') return;
        const rect = canvasRef.current.getBoundingClientRect();
        const clientX = e.clientX || (e.touches && e.touches[0].clientX);
        const clientY = e.clientY || (e.touches && e.touches[0].clientY);
        const x = clientX - rect.left;
        const y = clientY - rect.top;
        context.lineTo(x, y);
        context.stroke();
        setHasSignature(true);
    };

    const stopDrawing = () => {
        setIsDrawing(false);
    };

    const handleSave = () => {
        if (!hasSignature) {
            showToast('Atención', 'Por favor dibuja o sube una firma antes de guardar', 'warning');
            return;
        }

        canvasRef.current.toBlob((blob) => {
            if (!blob) {
                showToast('Error', 'No se pudo generar la imagen de la firma', 'error');
                return;
            }
            const file = new File([blob], 'firma_digital.png', { type: 'image/png' });
            onSave(file);
            onClose();
        }, 'image/png');
    };

    if (!isOpen) return null;

    return createPortal(
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 overflow-y-auto"
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-lg shadow-2xl border border-gray-100 dark:border-gray-700 transform transition-all my-8">
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-2">
                        <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-blue-600 dark:text-blue-400">
                            <Edit3 size={20} />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {title}
                        </h3>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Tabs de Selección */}
                <div className="flex space-x-2 p-1 bg-gray-100 dark:bg-gray-700/60 rounded-xl mb-4">
                    <button
                        type="button"
                        onClick={() => {
                            setActiveTab('draw');
                            if (rawImage) clearCanvas();
                        }}
                        className={`flex-1 flex items-center justify-center space-x-2 py-2 px-3 text-xs font-semibold rounded-lg transition-all ${
                            activeTab === 'draw'
                                ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                        }`}
                    >
                        <Edit3 size={15} />
                        <span>Dibujar Firma</span>
                    </button>

                    <button
                        type="button"
                        onClick={() => {
                            setActiveTab('upload');
                            if (!rawImage && fileInputRef.current) {
                                fileInputRef.current.click();
                            }
                        }}
                        className={`flex-1 flex items-center justify-center space-x-2 py-2 px-3 text-xs font-semibold rounded-lg transition-all ${
                            activeTab === 'upload'
                                ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                        }`}
                    >
                        <Upload size={15} />
                        <span>Subir Imagen</span>
                    </button>
                </div>

                {/* Mensaje Informativo */}
                <div className="mb-4 p-3 bg-blue-50/70 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/50 rounded-xl flex items-start space-x-3">
                    <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                    <p className="text-xs text-blue-900 dark:text-blue-200 leading-relaxed">
                        {activeTab === 'draw'
                            ? 'Dibuja tu firma en el recuadro usando tu mouse o pantalla táctil.'
                            : 'Puedes subir una foto o escaneo de tu firma y remover automáticamente el papel blanco.'}
                    </p>
                </div>

                {/* Zona de Trabajo / Canvas */}
                <div className="relative border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-2 mb-4 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] dark:bg-[radial-gradient(#374151_1px,transparent_1px)] [background-size:16px_16px] flex flex-col items-center justify-center min-h-[220px]">
                    <canvas
                        ref={canvasRef}
                        style={{ touchAction: 'none' }}
                        className="rounded cursor-crosshair max-w-full h-auto bg-white/40 dark:bg-gray-900/40"
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                    />

                    {/* Botón de Carga de Archivo si está en Tab Upload y no hay imagen cargada */}
                    {activeTab === 'upload' && !rawImage && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/90 dark:bg-gray-800/90 rounded-xl p-4 text-center">
                            <ImageIcon className="w-10 h-10 text-gray-400 mb-2" />
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Haz clic para seleccionar la imagen de tu firma
                            </p>
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow transition-colors flex items-center space-x-2"
                            >
                                <Upload size={14} />
                                <span>Buscar Archivo (PNG, JPG)</span>
                            </button>
                        </div>
                    )}

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png, image/jpeg, image/webp"
                        onChange={handleFileUpload}
                        className="hidden"
                    />
                </div>

                {/* Opciones de Eliminación de Fondo si hay imagen subida */}
                {activeTab === 'upload' && rawImage && (
                    <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600/60 space-y-3">
                        <div className="flex items-center justify-between">
                            <label className="flex items-center space-x-2 text-xs font-semibold text-gray-800 dark:text-gray-200 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={removeBg}
                                    onChange={(e) => setRemoveBg(e.target.checked)}
                                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                />
                                <span>Quitar fondo blanco/claro (Fondo Transparente)</span>
                            </label>
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center space-x-1"
                            >
                                <Upload size={12} />
                                <span>Cambiar foto</span>
                            </button>
                        </div>

                        {removeBg && (
                            <div className="pt-2 border-t border-gray-200 dark:border-gray-600/40">
                                <div className="flex justify-between items-center text-xs text-gray-600 dark:text-gray-400 mb-1">
                                    <span className="flex items-center space-x-1">
                                        <Sliders size={13} />
                                        <span>Sensibilidad de transparencia</span>
                                    </span>
                                    <span className="font-mono text-[11px] bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded">
                                        {bgThreshold}
                                    </span>
                                </div>
                                <input
                                    type="range"
                                    min="20"
                                    max="180"
                                    step="5"
                                    value={bgThreshold}
                                    onChange={(e) => setBgThreshold(Number(e.target.value))}
                                    className="w-full h-1.5 bg-gray-300 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-600"
                                />
                                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                                    Sube el valor si quedan restos de fondo, bájalo si se borra parte de la firma.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* Footer / Acciones */}
                <div className="flex justify-between items-center pt-2 border-t border-gray-100 dark:border-gray-700">
                    <button
                        type="button"
                        onClick={clearCanvas}
                        className="flex items-center space-x-1.5 px-3 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                        <RotateCcw size={14} />
                        <span>Limpiar</span>
                    </button>

                    <div className="flex space-x-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={!hasSignature}
                            className="flex items-center space-x-1.5 px-5 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            <Save size={15} />
                            <span>Guardar Firma</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default SignatureDrawer;